// What an experiment plan will do, as a time series for the designer's preview chart and the preset cards'
// sparklines. Pure - no React, no fetching. Times are minutes from the start.
//
// Stress plan: each step holds its target (or ramps linearly to it from the previous target); the water level
// follows the planned water events (relative cm moves, so it needs the starting level).
// Calibration plan: each condition is set and soaked, then the water runs the ladder; the durations come from the
// backend's estimate (POST /experiment/estimate/) when given, else typical values.

// The campaign does not wait for the chamber to settle (it settles while the water moves), so no soak by default.
const TYPICAL_SOAK_S = 0;
const TYPICAL_LEVEL_S = 900;

const round1 = (value) => Math.round(value * 10) / 10;
// An editor field: "" or null is "not entered yet", not 0.
const num = (value) => (value === "" || value === null || value === undefined ? NaN : Number(value));

export function stressPlanSeries(steps = [], waterEvents = [], { startLevelCm = null } = {}) {
  const points = [];
  let minute = 0;
  let previous = null;
  for (const step of steps) {
    const temp = num(step.temp_c ?? step.to_temp_c);
    const rh = num(step.rh_pct ?? step.to_rh_pct);
    const minutes = num(step.duration_seconds) / 60;
    if (!Number.isFinite(temp) || !Number.isFinite(rh) || !(minutes > 0)) continue;
    const from = step.kind === "ramp" && previous ? previous : { temp, rh };
    points.push({ minute: round1(minute), temp: from.temp, rh: from.rh });
    minute += minutes;
    points.push({ minute: round1(minute), temp, rh });
    previous = { temp, rh };
  }
  const start = Number(startLevelCm);
  if (Number.isFinite(start) && startLevelCm !== "" && startLevelCm !== null && points.length) {
    let level = start;
    const events = [...waterEvents]
      .map((e) => ({ at: Number(e.at_minute), cm: Number(e.cm), action: e.action }))
      .filter((e) => Number.isFinite(e.at) && Number.isFinite(e.cm))
      .sort((a, b) => a.at - b.at);
    const levelPoints = [{ minute: 0, level }];
    for (const event of events) {
      levelPoints.push({ minute: event.at, level });
      level = round1(level + (event.action === "drain" ? -event.cm : event.cm));
      levelPoints.push({ minute: event.at, level });
    }
    levelPoints.push({ minute: points[points.length - 1].minute, level });
    return mergeByMinute(points, levelPoints);
  }
  return points;
}

// The campaign as visits - the chamber at one condition while the water visits some levels - in order. MIRRORS the
// backend's rules.visits (padihat/experiment/rules.py): "condition_first" runs the whole ladder at each condition;
// "level_first" keeps the water at each level while the conditions run forwards, then backwards at the next level.
// With `alternate` (the backend's default) every other condition runs the ladder backwards: 15 -> 40, then 40 -> 15.
export function campaignVisits(conditions = [], ladder = {}) {
  const levels = ladder.levels_cm && ladder.levels_cm.length ? ladder.levels_cm : [];
  if (ladder.order !== "level_first") {
    const alternate = ladder.alternate ?? true;
    return conditions.map((condition, index) => ({ condition, levels: alternate && index % 2 ? [...levels].reverse() : levels }));
  }
  const visits = [];
  levels.forEach((level, index) => {
    const ordered = index % 2 === 0 ? conditions : [...conditions].reverse();
    ordered.forEach((condition) => visits.push({ condition, levels: [level] }));
  });
  return visits;
}

const sameCondition = (a, b) => a && b && Number(a.temp_c) === Number(b.temp_c) && Number(a.rh_pct) === Number(b.rh_pct);

export function calibrationPlanSeries(conditions = [], ladder = {}, estimate = null) {
  const visits = campaignVisits(conditions, ladder);
  const points = [];
  let minute = 0;
  let previous = null;
  let restLevel = visits.length && visits[0].levels.length ? visits[0].levels[0] : null;
  visits.forEach(({ condition, levels }, index) => {
    const temp = num(condition.temp_c);
    const rh = num(condition.rh_pct);
    if (!Number.isFinite(temp) || !Number.isFinite(rh)) return;
    const timing = estimate?.visits?.[index];
    const soak = (timing?.soak_s ?? (sameCondition(previous, condition) ? 0 : TYPICAL_SOAK_S)) / 60;
    const stairs = (timing?.staircase_s ?? levels.length * TYPICAL_LEVEL_S) / 60;
    points.push({ minute: round1(minute), temp, rh, level: restLevel });
    minute += soak;
    points.push({ minute: round1(minute), temp, rh, level: restLevel });
    const per = levels.length ? stairs / levels.length : 0;
    levels.forEach((level) => {
      points.push({ minute: round1(minute), temp, rh, level });
      minute += per;
      points.push({ minute: round1(minute), temp, rh, level });
      restLevel = level;
    });
    previous = condition;
  });
  return points;
}

// Merge two minute-keyed series (environment and water level), carrying the last known value of each forward so
// every row has both.
function mergeByMinute(env, level) {
  const minutes = [...new Set([...env.map((p) => p.minute), ...level.map((p) => p.minute)])].sort((a, b) => a - b);
  const valueAt = (series, key, m) => {
    let value = null;
    for (const point of series) {
      if (point.minute <= m) value = point[key];
      else break;
    }
    return value;
  };
  return minutes.map((m) => ({
    minute: m,
    temp: valueAt(env, "temp", m),
    rh: valueAt(env, "rh", m),
    level: valueAt(level, "level", m),
  }));
}

export function planMinutes(points) {
  return points.length ? points[points.length - 1].minute : 0;
}

export function formatDuration(seconds) {
  if (!Number.isFinite(seconds) || seconds <= 0) return "—";
  const minutes = Math.round(seconds / 60);
  if (minutes < 90) return `${minutes} min`;
  const hours = seconds / 3600;
  if (hours < 36) return `${Math.round(hours * 10) / 10} h`;
  return `${Math.round((hours / 24) * 10) / 10} days`;
}
